import {ParseResult, Schema} from "effect";

// ── Legacy domain-service id (bare slug, as carried on the wire) ────────

/**
 * A legacy domain-service id is a bare lowercase slug (e.g. `"drift"`,
 * `"auto-layout"`) — the unprefixed form a domain service is identified by
 * on the JWT wire today (the `use_case` claim minted by backend services).
 *
 * A "domain service" is a named, logical, bounded capability that can act on
 * behalf of the system/user (e.g. the drift agent, auto-layout). It is
 * deliberately decoupled from the runtime container it happens to be deployed
 * in — one container can host many domain services, and a domain service can
 * move between containers.
 *
 * This brand is *open*: any well-formed slug is accepted. It carries no
 * knowledge of which domain services actually exist — that closed enumeration
 * lives in the `auth` domain (`KnownDomainService`), the only place that
 * differentiates between specific services. To every other consumer a
 * domain-service id is existential ("some service"), carried/stored/logged
 * without case analysis.
 */
const LegacyDomainServiceIdSchema = Schema.String.pipe(
    Schema.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    Schema.brand("LegacyDomainServiceId"),
);

/**
 * @deprecated Prefer {@link PrefixedDomainServiceId} (`svcid_` format) for new
 * code and storage; the bare form exists for JWT wire compatibility.
 */
export type LegacyDomainServiceId = typeof LegacyDomainServiceIdSchema.Type;

/**
 * Type guard — decides whether a value is a LegacyDomainServiceId.
 *
 * @deprecated Prefer {@link PrefixedDomainServiceId} (`svcid_` format) for new
 * code and storage; the bare form exists for JWT wire compatibility.
 */
export const isLegacyDomainServiceId = Schema.is(LegacyDomainServiceIdSchema);

// ── Prefixed domain-service id (new canonical form) ─────────────────────

/**
 * A prefixed domain-service id is a string that starts with `"svcid_"`
 * followed by the bare slug (e.g. `"svcid_drift"`).
 *
 * The `svcid_` prefix is what keeps a domain-service id from being a *truly*
 * plain string: a value found loose in logs or storage is self-identifying
 * and round-trips deserialization safely. This mirrors `PrefixedUserUid`'s
 * `usr_` convention exactly (open after the prefix; the closed set of
 * recognized values is an `auth`-domain concern).
 */
const PrefixedDomainServiceIdSchema = Schema.TemplateLiteral("svcid_", Schema.String).pipe(
    Schema.brand("PrefixedDomainServiceId"),
);

export type PrefixedDomainServiceId = typeof PrefixedDomainServiceIdSchema.Type;

/**
 * Type guard — decides whether a string is a branded PrefixedDomainServiceId.
 */
export const isPrefixedDomainServiceId = Schema.is(PrefixedDomainServiceIdSchema);

// ── Canonical inverse pair: Legacy ↔ Prefixed ──────────────────────────

/**
 * `Schema.transformOrFail` bundling the inverse pair
 * `LegacyDomainServiceId ↔ PrefixedDomainServiceId`.
 *
 * Bundling both directions in a single carrier prevents the two halves from
 * drifting and lets callers compose the conversion into Effect Schema
 * pipelines (e.g. `Schema.encode`, `Schema.decode`, struct fields) instead of
 * calling matched standalone helpers. The `auth` domain composes this into its
 * service-token claim codec so the canonical `svcid_`-prefixed form is used
 * in memory while the bare slug stays on the JWT wire (no wire break for
 * in-flight tokens).
 *
 * - decode (Legacy → Prefixed): prepend `"svcid_"` to a
 *   `LegacyDomainServiceId`. Always succeeds for any value the source schema
 *   accepts.
 * - encode (Prefixed → Legacy): strip the `"svcid_"` prefix from a
 *   `PrefixedDomainServiceId`. The stripped value must satisfy
 *   `LegacyDomainServiceId`'s slug invariant; if it does not (e.g. encoding
 *   `"svcid_"` or `"svcid_Drift"`), the transform fails with a typed
 *   `ParseResult.Type` issue.
 */
export const LegacyDomainServiceIdToPrefixedDomainServiceId = Schema.transformOrFail(
    LegacyDomainServiceIdSchema,
    PrefixedDomainServiceIdSchema,
    {
        strict: true,
        decode: (legacy) => ParseResult.succeed(`svcid_${legacy}` as PrefixedDomainServiceId),
        encode: (prefixed, options, ast) => {
            const stripped = prefixed.slice("svcid_".length);
            // Delegate to the source schema's decoder so any future constraint
            // added to `LegacyDomainServiceIdSchema` (charset, length, etc.) is
            // automatically enforced on the encode direction without duplication.
            return ParseResult.mapError(
                Schema.decode(LegacyDomainServiceIdSchema)(stripped, options),
                (issue) =>
                    new ParseResult.Type(
                        ast,
                        prefixed,
                        `Stripped domain-service id "${stripped}" does not satisfy LegacyDomainServiceId: ${issue.message}`,
                    ),
            );
        },
    },
);

// ── Conversions (idempotent convenience helpers) ───────────────────────

/**
 * Convert any domain-service id string to a {@link PrefixedDomainServiceId}.
 * Idempotent — if the value already has the `"svcid_"` prefix, it is returned
 * as-is.
 *
 * Idempotent semantics let callers safely re-prefix mixed inputs at API
 * boundaries; the canonical strict inverse is
 * {@link LegacyDomainServiceIdToPrefixedDomainServiceId}.
 */
export function toPrefixedDomainServiceId(id: string): PrefixedDomainServiceId {
    return (isPrefixedDomainServiceId(id) ? id : `svcid_${id}`) as PrefixedDomainServiceId;
}

/**
 * Extract the bare slug from a {@link PrefixedDomainServiceId} by stripping the
 * `"svcid_"` prefix. If the value does not have the prefix, it is returned
 * as-is.
 *
 * Returns a plain `string` (not a branded `LegacyDomainServiceId`) to match the
 * shape of {@link parseUserUidFromPrefixed}: stripping the prefix can produce a
 * value that violates the bare slug invariant (e.g. `"svcid_"` → `""`), so
 * callers that need a branded `LegacyDomainServiceId` must validate via
 * {@link isLegacyDomainServiceId}, `LegacyDomainServiceId.schema`, or use
 * {@link LegacyDomainServiceIdToPrefixedDomainServiceId}'s `encode` direction
 * (which surfaces the violation as a typed parse error).
 */
export function parseDomainServiceIdFromPrefixed(id: string): string {
    return isPrefixedDomainServiceId(id) ? id.slice("svcid_".length) : id;
}

// ── Companion objects ───────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const LegacyDomainServiceId = {
    schema: LegacyDomainServiceIdSchema,
    isLegacyDomainServiceId,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PrefixedDomainServiceId = {
    schema: PrefixedDomainServiceIdSchema,
    isPrefixedDomainServiceId,
    toPrefixedDomainServiceId,
    parseDomainServiceIdFromPrefixed,
    /**
     * Canonical strict inverse — see
     * {@link LegacyDomainServiceIdToPrefixedDomainServiceId}.
     */
    fromLegacy: LegacyDomainServiceIdToPrefixedDomainServiceId,
} as const;
